import { gql } from '@apollo/client';
import { FODBOLDHOLD_TEAM_FIELDS } from './queries';

/**
 * GraphQL mutation til at logge en bruger ind
 * @param email - Brugerens email adresse
 * @param password - Brugerens adgangskode
 * @returns Token og brugeroplysninger (id, email, navn)
 */
export const LOGIN = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      mate {
        id
        email
        name
        role
        lastLogin
        createdAt
        updatedAt
      }
    }
  }
`;

/**
 * GraphQL mutation til at registrere en ny bruger
 * @param email - Den nye brugers email adresse
 * @param password - Den nye brugers adgangskode
 * @param name - Den nye brugers navn
 * @param signupToken - Signup token hvis registrering kræver det
 * @returns Token og brugeroplysninger (id, email, navn, role)
 */
export const REGISTER = gql`
  mutation Register($email: String!, $password: String!, $name: String, $signupToken: String, $holdInvite: String) {
    register(email: $email, password: $password, name: $name, signupToken: $signupToken, holdInvite: $holdInvite) {
      token
      mate {
        id
        email
        name
        role
      }
    }
  }
`;

/**
 * GraphQL mutation til at oprette et nyt signup link (kun admin)
 * @returns Det nye signup link
 */
export const CREATE_SIGNUP_LINK = gql`
  mutation CreateSignupLink {
    createSignupLink {
      id
      token
      isUsed
      usedBy
      createdBy
      createdAt
      expiresAt
      updatedAt
      isValid
    }
  }
`;

/**
 * GraphQL mutation til at tilbagekalde et signup link (kun admin)
 * @param id - ID af signup linket der skal tilbagekaldes
 * @returns Boolean der indikerer success
 */
export const REVOKE_SIGNUP_LINK = gql`
  mutation RevokeSignupLink($id: ID!) {
    revokeSignupLink(id: $id)
  }
`;

/**
 * GraphQL mutation til at opdatere profil
 * @param name - Brugerens nye navn
 * @returns Den opdaterede bruger
 */
export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($name: String!) {
    updateProfile(name: $name) {
      id
      email
      name
      role
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL mutation til at skifte password
 * @param currentPassword - Brugerens nuværende adgangskode
 * @param newPassword - Den nye adgangskode
 * @returns Boolean der indikerer success
 */
export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

/**
 * GraphQL mutation til at slette en bruger (kun admin)
 * @param id - ID af brugeren der skal slettes
 * @returns Boolean der indikerer success
 */
export const DELETE_MATE = gql`
  mutation DeleteMate($id: ID!) {
    deleteMate(id: $id)
  }
`;

/**
 * GraphQL mutation til at ændre admin status for en bruger (kun admin)
 * @param id - ID af brugeren der skal ændres
 * @param isAdmin - Om brugeren skal have admin rettigheder
 * @returns Den opdaterede bruger
 */
export const TOGGLE_MATE_ADMIN = gql`
  mutation ToggleMateAdmin($id: ID!, $isAdmin: Boolean!) {
    toggleMateAdmin(id: $id, isAdmin: $isAdmin) {
      id
      email
      name
      role
      pageAccess
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_MATE_PAGE_ACCESS = gql`
  mutation UpdateMatePageAccess(
    $id: ID!
    $pageAccess: [String!]!
  ) {
    updateMatePageAccess(
      id: $id
      pageAccess: $pageAccess
    ) {
      id
      email
      name
      role
      pageAccess
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_CHAMPIONS_LEAGUE_PHASE_LOCKS = gql`
  mutation UpdateChampionsLeaguePhaseLocks($phaseLocks: PhaseLocksInput!) {
    updateChampionsLeaguePhaseLocks(phaseLocks: $phaseLocks) {
      id
      phaseLocks {
        phase0
        phase1
        phase2
        phase2Playoff
        phase2Round16
        phase2Quarters
        phase2Semis
        phase3
      }
      updatedAt
    }
  }
`;

export const UPDATE_CHAMPIONS_LEAGUE_REAL_RESULTS = gql`
  mutation UpdateChampionsLeagueRealResults($realResults: RealResultsInput!) {
    updateChampionsLeagueRealResults(realResults: $realResults) {
      id
      realResults {
        leagueStanding
        playoffResults {
          id
          home
          away
          winner
          homeScore
          awayScore
        }
        roundOf16Results {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
        quarterFinalResults {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
        semiFinalResults {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
        finalResult {
          id
          home
          away
          winner
          homeScore
          awayScore
          aggregateScore
        }
      }
      updatedAt
    }
  }
`;

export const SAVE_CHAMPIONS_LEAGUE_GAME_DATA = gql`
  mutation SaveChampionsLeagueGameData($gameData: ChampionsLeagueGameDataInput!) {
    saveChampionsLeagueGameData(gameData: $gameData) {
      id
      mateId
      preseasonWinner
      leagueStanding
      playoffPredictions
      knockoutPredictions
      finalPrediction
      points
      phase0Points
      phase1Points
      phase2Points
      phase3Points
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_CHAMPIONS_LEAGUE_GAME_DATA = gql`
  mutation UpdateChampionsLeagueGameData($gameData: ChampionsLeagueGameDataInput!) {
    updateChampionsLeagueGameData(gameData: $gameData) {
      id
      mateId
      preseasonWinner
      leagueStanding
      playoffPredictions
      knockoutPredictions
      finalPrediction
      points
      phase0Points
      phase1Points
      phase2Points
      phase3Points
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_CHAMPIONS_LEAGUE_GAME_DATA_FOR_MATE = gql`
  mutation UpdateChampionsLeagueGameDataForMate($mateId: ID!, $gameData: ChampionsLeagueGameDataInput!) {
    updateChampionsLeagueGameDataForMate(mateId: $mateId, gameData: $gameData) {
      id
      mateId
      preseasonWinner
      leagueStanding
      playoffPredictions
      knockoutPredictions
      finalPrediction
      points
      phase0Points
      phase1Points
      phase2Points
      phase3Points
      createdAt
      updatedAt
    }
  }
`;

export const RECALCULATE_ALL_POINTS = gql`
  mutation RecalculateAllPoints {
    recalculateAllPoints {
      success
      message
      updatedCount
    }
  }
`;

export const CREATE_CHAMPIONS_LEAGUE_TEAMS = gql`
  mutation CreateChampionsLeagueTeams($teams: [TeamInput!]!, $season: String!) {
    createChampionsLeagueTeams(teams: $teams, season: $season) {
      id
      teams {
        name
        points
        position
      }
      season
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_CHAMPIONS_LEAGUE_TEAMS = gql`
  mutation UpdateChampionsLeagueTeams($teams: [TeamInput!]!) {
    updateChampionsLeagueTeams(teams: $teams) {
      id
      teams {
        name
        points
        position
      }
      season
      isActive
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL mutation til at oprette et password reset link (kun admin)
 * @param mateId - ID af brugeren der skal nulstille password
 * @returns Det nye password reset link med autogenereret password
 */
export const CREATE_PASSWORD_RESET_LINK = gql`
  mutation CreatePasswordResetLink($mateId: ID!) {
    createPasswordResetLink(mateId: $mateId) {
      id
      token
      isUsed
      usedBy
      mateId
      createdBy
      createdAt
      expiresAt
      updatedAt
      isValid
      newPassword
    }
  }
`;

/**
 * GraphQL mutation til at nulstille password med et token
 * @param token - Token fra password reset linket
 * @returns Resultat med det nye password og brugeroplysninger
 */
export const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!) {
    resetPassword(token: $token) {
      success
      newPassword
      mate {
        id
        email
        name
        role
        lastLogin
        createdAt
        updatedAt
      }
    }
  }
`;

/**
 * GraphQL mutation til at tilbagekalde et password reset link (kun admin)
 * @param id - ID af password reset linket der skal tilbagekaldes
 * @returns Boolean der indikerer success
 */
export const REVOKE_PASSWORD_RESET_LINK = gql`
  mutation RevokePasswordResetLink($id: ID!) {
    revokePasswordResetLink(id: $id)
  }
`; 

export const UPSERT_LANDSKAMP_MATCH = gql`
  mutation UpsertLandskampMatch($input: LandskampMatchInput!) {
    upsertLandskampMatch(input: $input) {
      id
      kickoff
      homeTeam
      awayTeam
      competition
      venue
      city
      homeScore
      awayScore
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_LANDSKAMP_MATCH = gql`
  mutation DeleteLandskampMatch($id: ID!) {
    deleteLandskampMatch(id: $id)
  }
`;

export const UPSERT_MY_LANDSKAMP_GUESS = gql`
  mutation UpsertMyLandskampGuess($matchId: ID!, $homeScore: Int!, $awayScore: Int!) {
    upsertMyLandskampGuess(matchId: $matchId, homeScore: $homeScore, awayScore: $awayScore) {
      id
      matchId
      mateId
      mateName
      homeScore
      awayScore
      createdAt
      updatedAt
    }
  }
`;

export const UPSERT_KAMPDAG_MATCH = gql`
  mutation UpsertKampdagMatch($input: KampdagMatchInput!) {
    upsertKampdagMatch(input: $input) {
      id
      title
      kickoff
      homeTeam
      awayTeam
      venue
      city
      enableResult
      enableYellowCards
      enableRedCards
      enableCorners
      homeScore
      awayScore
      yellowCards
      redCards
      corners
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_KAMPDAG_MATCH = gql`
  mutation DeleteKampdagMatch($id: ID!) {
    deleteKampdagMatch(id: $id)
  }
`;

export const UPSERT_MY_KAMPDAG_GUESS = gql`
  mutation UpsertMyKampdagGuess($input: KampdagGuessInput!) {
    upsertMyKampdagGuess(input: $input) {
      id
      matchId
      mateId
      mateName
      homeScore
      awayScore
      yellowCards
      redCards
      corners
      createdAt
      updatedAt
    }
  }
`;

export const REGISTER_PUSH_SUBSCRIPTION = gql`
  mutation RegisterPushSubscription($subscription: PushSubscriptionInput!) {
    registerPushSubscription(subscription: $subscription)
  }
`;

export const UNREGISTER_PUSH_SUBSCRIPTION = gql`
  mutation UnregisterPushSubscription($endpoint: String!) {
    unregisterPushSubscription(endpoint: $endpoint)
  }
`;

export const SEND_PUSH_NOTIFICATION_TO_ALL = gql`
  mutation SendPushNotificationToAll($title: String!, $body: String!, $url: String) {
    sendPushNotificationToAll(title: $title, body: $body, url: $url) {
      sent
      failed
    }
  }
`;

export const SEND_PUSH_NOTIFICATION_TO_MATES = gql`
  mutation SendPushNotificationToMates($mateIds: [ID!]!, $title: String!, $body: String!, $url: String) {
    sendPushNotificationToMates(mateIds: $mateIds, title: $title, body: $body, url: $url) {
      sent
      failed
    }
  }
`;

export const CREATE_WORLD_CUP_GUESS = gql`
  mutation CreateWorldCupGuess($input: WorldCupGuessInput!) {
    createWorldCupGuess(input: $input) {
      id
      matchId
      homeGoals
      awayGoals
      isJoker
      pointsAwarded
    }
  }
`;

export const SAVE_WORLD_CHAMPION_GUESS = gql`
  mutation SaveWorldChampionGuess($championTeam: String!) {
    saveWorldChampionGuess(championTeam: $championTeam) {
      id
      championTeam
      pointsAwarded
      locked
      updatedAt
    }
  }
`;

export const CREATE_WORLD_CUP_LEAGUE = gql`
  mutation CreateWorldCupLeague($name: String!) {
    createWorldCupLeague(name: $name) {
      id
      name
      ownerId
      inviteCode
      createdAt
    }
  }
`;

export const JOIN_WORLD_CUP_LEAGUE = gql`
  mutation JoinWorldCupLeague($inviteCode: String!) {
    joinWorldCupLeague(inviteCode: $inviteCode) {
      id
      name
      inviteCode
      createdAt
    }
  }
`;

export const LEAVE_WORLD_CUP_LEAGUE = gql`
  mutation LeaveWorldCupLeague($leagueId: ID!) {
    leaveWorldCupLeague(leagueId: $leagueId)
  }
`;

export const CREATE_WORLD_CUP_MATCH = gql`
  mutation CreateWorldCupMatch($input: WorldCupMatchInput!) {
    createWorldCupMatch(input: $input) {
      id
      homeTeam
      awayTeam
      kickoffTime
      stage
      sortOrder
      homeGoals
      awayGoals
      status
    }
  }
`;

export const UPDATE_WORLD_CUP_MATCH = gql`
  mutation UpdateWorldCupMatch($input: WorldCupMatchInput!) {
    updateWorldCupMatch(input: $input) {
      id
      homeTeam
      awayTeam
      kickoffTime
      stage
      sortOrder
      homeGoals
      awayGoals
      status
    }
  }
`;

export const DELETE_WORLD_CUP_MATCH = gql`
  mutation DeleteWorldCupMatch($id: ID!) {
    deleteWorldCupMatch(id: $id)
  }
`;

export const SET_WORLD_CUP_RESULT = gql`
  mutation SetWorldCupResult($matchId: ID!, $homeGoals: Int!, $awayGoals: Int!) {
    setWorldCupResult(matchId: $matchId, homeGoals: $homeGoals, awayGoals: $awayGoals) {
      id
      homeGoals
      awayGoals
      status
    }
  }
`;

export const SET_WORLD_CHAMPION = gql`
  mutation SetWorldChampion($championTeam: String!) {
    setWorldChampion(championTeam: $championTeam)
  }
`;

export const UPDATE_WORLD_CUP_TEAMS = gql`
  mutation UpdateWorldCupTeams($teams: [WorldCupTeamInput!]!) {
    updateWorldCupTeams(teams: $teams) {
      id
      teams {
        name
        group
        flagCode
      }
      updatedAt
    }
  }
`;

const fodboldholdTeam = `
  ...FodboldholdTeamFields
`;

export const CREATE_FODBOLDHOLD = gql`
  mutation CreateFodboldhold($name: String!, $seasonName: String!) {
    createFodboldhold(name: $name, seasonName: $seasonName) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const CREATE_FODBOLDHOLD_INVITE = gql`
  mutation CreateFodboldholdInvite($teamId: ID!, $hours: Int!, $maxUses: Int) {
    createFodboldholdInvite(teamId: $teamId, hours: $hours, maxUses: $maxUses) {
      id
      token
      expiresAt
      maxUses
      usedCount
      active
      createdAt
    }
  }
`;

export const SET_FODBOLDHOLD_INVITE_ACTIVE = gql`
  mutation SetFodboldholdInviteActive($id: ID!, $active: Boolean!) {
    setFodboldholdInviteActive(id: $id, active: $active) {
      id
      active
    }
  }
`;

export const DELETE_FODBOLDHOLD_INVITE = gql`
  mutation DeleteFodboldholdInvite($id: ID!) {
    deleteFodboldholdInvite(id: $id)
  }
`;

export const JOIN_FODBOLDHOLD_INVITE = gql`
  mutation JoinFodboldholdInvite($token: String!) {
    joinFodboldholdInvite(token: $token) {
      id
    }
  }
`;

export const ADD_FODBOLDHOLD_MEMBER = gql`
  mutation AddFodboldholdMember($teamId: ID!, $mateId: ID!) {
    addFodboldholdMember(teamId: $teamId, mateId: $mateId) {
      id
    }
  }
`;

export const REMOVE_FODBOLDHOLD_MEMBER = gql`
  mutation RemoveFodboldholdMember($teamId: ID!, $mateId: ID!) {
    removeFodboldholdMember(teamId: $teamId, mateId: $mateId) {
      id
    }
  }
`;

export const JOIN_FODBOLDHOLD = gql`
  mutation JoinFodboldhold($inviteCode: String!) {
    joinFodboldhold(inviteCode: $inviteCode) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD = gql`
  mutation UpdateFodboldhold($teamId: ID!, $input: FodboldholdTeamInput!) {
    updateFodboldhold(teamId: $teamId, input: $input) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_SEASON = gql`
  mutation AddFodboldholdSeason($teamId: ID!, $name: String!) {
    addFodboldholdSeason(teamId: $teamId, name: $name) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_ADMIN = gql`
  mutation SetFodboldholdAdmin($teamId: ID!, $mateId: ID!, $isAdmin: Boolean!) {
    setFodboldholdAdmin(teamId: $teamId, mateId: $mateId, isAdmin: $isAdmin) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_FINE_CHAIR = gql`
  mutation SetFodboldholdFineChair($teamId: ID!, $mateId: ID!, $fineChair: Boolean!) {
    setFodboldholdFineChair(teamId: $teamId, mateId: $mateId, fineChair: $fineChair) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_MY_FODBOLDHOLD_FINE_BOX = gql`
  mutation SetMyFodboldholdFineBox($teamId: ID!, $inFineBox: Boolean!) {
    setMyFodboldholdFineBox(teamId: $teamId, inFineBox: $inFineBox) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const CREATE_FODBOLDHOLD_FINE_TYPE = gql`
  mutation CreateFodboldholdFineType($teamId: ID!, $text: String!, $amount: Int!) {
    createFodboldholdFineType(teamId: $teamId, text: $text, amount: $amount) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD_FINE_TYPE = gql`
  mutation UpdateFodboldholdFineType($teamId: ID!, $typeId: ID!, $text: String!, $amount: Int!) {
    updateFodboldholdFineType(teamId: $teamId, typeId: $typeId, text: $text, amount: $amount) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const DELETE_FODBOLDHOLD_FINE_TYPE = gql`
  mutation DeleteFodboldholdFineType($teamId: ID!, $typeId: ID!) {
    deleteFodboldholdFineType(teamId: $teamId, typeId: $typeId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const GIVE_FODBOLDHOLD_FINES = gql`
  mutation GiveFodboldholdFines($teamId: ID!, $seasonId: ID!, $typeId: ID, $text: String, $amount: Int, $mateIds: [ID!]!, $matchId: ID, $count: Int) {
    giveFodboldholdFines(teamId: $teamId, seasonId: $seasonId, typeId: $typeId, text: $text, amount: $amount, mateIds: $mateIds, matchId: $matchId, count: $count) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD_FINE_GROUP = gql`
  mutation UpdateFodboldholdFineGroup($teamId: ID!, $fineIds: [ID!]!, $text: String!, $amount: Int!, $count: Int!) {
    updateFodboldholdFineGroup(teamId: $teamId, fineIds: $fineIds, text: $text, amount: $amount, count: $count) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const DELETE_FODBOLDHOLD_FINE = gql`
  mutation DeleteFodboldholdFine($teamId: ID!, $fineId: ID!) {
    deleteFodboldholdFine(teamId: $teamId, fineId: $fineId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_FINE_PAID = gql`
  mutation SetFodboldholdFinePaid($teamId: ID!, $fineId: ID!, $paid: Boolean!) {
    setFodboldholdFinePaid(teamId: $teamId, fineId: $fineId, paid: $paid) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_FINES_PAID = gql`
  mutation SetFodboldholdFinesPaid($teamId: ID!, $seasonId: ID!, $mateId: ID!) {
    setFodboldholdFinesPaid(teamId: $teamId, seasonId: $seasonId, mateId: $mateId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_FINE_BOX = gql`
  mutation SetFodboldholdFineBox($teamId: ID!, $link: String!) {
    setFodboldholdFineBox(teamId: $teamId, link: $link) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const CLAIM_FODBOLDHOLD_FINE_PAYMENT = gql`
  mutation ClaimFodboldholdFinePayment($teamId: ID!, $seasonId: ID!) {
    claimFodboldholdFinePayment(teamId: $teamId, seasonId: $seasonId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const DECIDE_FODBOLDHOLD_FINE_CLAIM = gql`
  mutation DecideFodboldholdFineClaim($teamId: ID!, $claimId: ID!, $approve: Boolean!) {
    decideFodboldholdFineClaim(teamId: $teamId, claimId: $claimId, approve: $approve) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_FINE_PAYMENT = gql`
  mutation SetFodboldholdFinePayment($teamId: ID!, $seasonId: ID!, $mateId: ID!, $amount: Int!, $entry: Int) {
    setFodboldholdFinePayment(teamId: $teamId, seasonId: $seasonId, mateId: $mateId, amount: $amount, entry: $entry) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SEND_FODBOLDHOLD_NOTICES = gql`
  mutation SendFodboldholdNotices($teamId: ID!, $notices: [FodboldholdNoticeInput!]!) {
    sendFodboldholdNotices(teamId: $teamId, notices: $notices) {
      sent
      failed
    }
  }
`;

export const SET_FODBOLDHOLD_FEE = gql`
  mutation SetFodboldholdFee($teamId: ID!, $seasonId: ID!, $amount: Int!, $due: String) {
    setFodboldholdFee(teamId: $teamId, seasonId: $seasonId, amount: $amount, due: $due) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_STANDINGS_URL = gql`
  mutation SetFodboldholdStandingsUrl($teamId: ID!, $seasonId: ID!, $url: String!) {
    setFodboldholdStandingsUrl(teamId: $teamId, seasonId: $seasonId, url: $url) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const TOGGLE_FODBOLDHOLD_PAID = gql`
  mutation ToggleFodboldholdPaid($teamId: ID!, $seasonId: ID!, $mateId: ID!) {
    toggleFodboldholdPaid(teamId: $teamId, seasonId: $seasonId, mateId: $mateId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const IMPORT_FODBOLDHOLD_MATCHES = gql`
  mutation ImportFodboldholdMatches($teamId: ID!, $seasonId: ID!) {
    importFodboldholdMatches(teamId: $teamId, seasonId: $seasonId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_MATCH = gql`
  mutation AddFodboldholdMatch($teamId: ID!, $seasonId: ID!, $input: FodboldholdMatchInput!) {
    addFodboldholdMatch(teamId: $teamId, seasonId: $seasonId, input: $input) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD_MATCH = gql`
  mutation UpdateFodboldholdMatch($teamId: ID!, $matchId: ID!, $input: FodboldholdMatchInput!) {
    updateFodboldholdMatch(teamId: $teamId, matchId: $matchId, input: $input) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const TOGGLE_FODBOLDHOLD_MATCH = gql`
  mutation ToggleFodboldholdMatch($teamId: ID!, $matchId: ID!) {
    toggleFodboldholdMatch(teamId: $teamId, matchId: $matchId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_MATCH_REPLY = gql`
  mutation SetFodboldholdMatchReply($teamId: ID!, $matchId: ID!, $reply: String!) {
    setFodboldholdMatchReply(teamId: $teamId, matchId: $matchId, reply: $reply) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_GOAL = gql`
  mutation SetFodboldholdGoal($teamId: ID!, $matchId: ID!, $playerId: ID!, $count: Int!) {
    setFodboldholdGoal(teamId: $teamId, matchId: $matchId, playerId: $playerId, count: $count) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const TOGGLE_FODBOLDHOLD_MATCH_MESSAGE_LIKE = gql`
  mutation ToggleFodboldholdMatchMessageLike($teamId: ID!, $messageId: ID!) {
    toggleFodboldholdMatchMessageLike(teamId: $teamId, messageId: $messageId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_MATCH_COMMENT = gql`
  mutation AddFodboldholdMatchComment($teamId: ID!, $messageId: ID!, $text: String!) {
    addFodboldholdMatchComment(teamId: $teamId, messageId: $messageId, text: $text) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_MATCH_MESSAGE = gql`
  mutation AddFodboldholdMatchMessage($teamId: ID!, $matchId: ID!, $text: String, $image: String) {
    addFodboldholdMatchMessage(teamId: $teamId, matchId: $matchId, text: $text, image: $image) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD_MATCH_MESSAGE = gql`
  mutation UpdateFodboldholdMatchMessage($teamId: ID!, $messageId: ID!, $text: String, $image: String) {
    updateFodboldholdMatchMessage(teamId: $teamId, messageId: $messageId, text: $text, image: $image) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const DELETE_FODBOLDHOLD_MATCH_MESSAGE = gql`
  mutation DeleteFodboldholdMatchMessage($teamId: ID!, $messageId: ID!) {
    deleteFodboldholdMatchMessage(teamId: $teamId, messageId: $messageId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_EVENT_MESSAGE = gql`
  mutation AddFodboldholdEventMessage($teamId: ID!, $eventId: ID!, $text: String, $image: String) {
    addFodboldholdEventMessage(teamId: $teamId, eventId: $eventId, text: $text, image: $image) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_EVENT = gql`
  mutation AddFodboldholdEvent($teamId: ID!, $seasonId: ID!, $input: FodboldholdEventInput!) {
    addFodboldholdEvent(teamId: $teamId, seasonId: $seasonId, input: $input) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const TOGGLE_FODBOLDHOLD_EVENT = gql`
  mutation ToggleFodboldholdEvent($teamId: ID!, $eventId: ID!) {
    toggleFodboldholdEvent(teamId: $teamId, eventId: $eventId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const SET_FODBOLDHOLD_EVENT_REPLY = gql`
  mutation SetFodboldholdEventReply($teamId: ID!, $eventId: ID!, $reply: String!) {
    setFodboldholdEventReply(teamId: $teamId, eventId: $eventId, reply: $reply) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_POST = gql`
  mutation AddFodboldholdPost($teamId: ID!, $text: String, $image: String) {
    addFodboldholdPost(teamId: $teamId, text: $text, image: $image) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD_POST = gql`
  mutation UpdateFodboldholdPost($teamId: ID!, $postId: ID!, $text: String, $image: String) {
    updateFodboldholdPost(teamId: $teamId, postId: $postId, text: $text, image: $image) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const TOGGLE_FODBOLDHOLD_POST_LIKE = gql`
  mutation ToggleFodboldholdPostLike($teamId: ID!, $postId: ID!) {
    toggleFodboldholdPostLike(teamId: $teamId, postId: $postId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const DELETE_FODBOLDHOLD_POST = gql`
  mutation DeleteFodboldholdPost($teamId: ID!, $postId: ID!) {
    deleteFodboldholdPost(teamId: $teamId, postId: $postId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const ADD_FODBOLDHOLD_COMMENT = gql`
  mutation AddFodboldholdComment($teamId: ID!, $postId: ID!, $text: String!) {
    addFodboldholdComment(teamId: $teamId, postId: $postId, text: $text) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_FODBOLDHOLD_COMMENT = gql`
  mutation UpdateFodboldholdComment($teamId: ID!, $commentId: ID!, $text: String!) {
    updateFodboldholdComment(teamId: $teamId, commentId: $commentId, text: $text) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const DELETE_FODBOLDHOLD_COMMENT = gql`
  mutation DeleteFodboldholdComment($teamId: ID!, $commentId: ID!) {
    deleteFodboldholdComment(teamId: $teamId, commentId: $commentId) {
      ${fodboldholdTeam}
    }
  }
  ${FODBOLDHOLD_TEAM_FIELDS}
`;

export const UPDATE_WORLD_CUP_STAGES = gql`
  mutation UpdateWorldCupStages($stages: [WorldCupStageInput!]!) {
    updateWorldCupStages(stages: $stages) {
      name
      sortOrder
    }
  }
`;